import { FC } from "react";
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import { useTranslation } from "react-i18next";
import { RepresentantSites } from "../slices/representantSitesSlice";

interface RepresentantSitesTableProps {
  rows: RepresentantSites[];
  onEdit: (representant: RepresentantSites) => void;
  onDelete: (representant: RepresentantSites) => void;
  onToggleStatus: (representant: RepresentantSites) => void;
}

const headerCellSx = {
  color: "#C1C1D0",
  fontFamily: "Poppins",
  textTransform: "uppercase" as const,
};

const SiteChips: FC<{ codes: string[]; emptyLabel: string }> = ({
  codes,
  emptyLabel,
}) => {
  if (codes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {codes.map((code) => (
        <Chip key={code} label={code} size="small" />
      ))}
    </Stack>
  );
};

const RepresentantSitesTable: FC<RepresentantSitesTableProps> = ({
  rows,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const { t } = useTranslation();
  const none = t("administration.representantSites.none");

  return (
    <TableContainer component={Paper} elevation={0} sx={{ minHeight: "50vh" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>
              {t("administration.representantSites.columns.representant")}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("administration.representantSites.columns.code")}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("administration.representantSites.columns.status")}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("administration.representantSites.columns.salesSites")}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("administration.representantSites.columns.expSites")}
            </TableCell>
            <TableCell sx={headerCellSx} align="center">
              {t("administration.representantSites.columns.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                {t("administration.representantSites.empty")}
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.userId} hover sx={{ opacity: row.isActive ? 1 : 0.6 }}>
              <TableCell>{row.username}</TableCell>
              <TableCell>{row.businessId || "-"}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={row.isActive ? "success" : "default"}
                  variant={row.isActive ? "filled" : "outlined"}
                  label={
                    row.isActive
                      ? t("administration.representantSites.status.active")
                      : t("administration.representantSites.status.archived")
                  }
                />
              </TableCell>
              <TableCell>
                <SiteChips codes={row.sitesVente} emptyLabel={none} />
              </TableCell>
              <TableCell>
                <SiteChips codes={row.sitesExpedition} emptyLabel={none} />
              </TableCell>
              <TableCell align="center">
                <Stack direction="row" justifyContent="center">
                  <Tooltip title={t("administration.representantSites.edit")}>
                    <IconButton
                      aria-label={t("administration.representantSites.edit")}
                      onClick={() => onEdit(row)}
                      size="small"
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      row.isActive
                        ? t("administration.representantSites.archive")
                        : t("administration.representantSites.unarchive")
                    }
                  >
                    <IconButton
                      aria-label={
                        row.isActive
                          ? t("administration.representantSites.archive")
                          : t("administration.representantSites.unarchive")
                      }
                      onClick={() => onToggleStatus(row)}
                      size="small"
                    >
                      {row.isActive ? <ArchiveOutlinedIcon /> : <UnarchiveOutlinedIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("administration.representantSites.delete")}>
                    <IconButton
                      aria-label={t("administration.representantSites.delete")}
                      onClick={() => onDelete(row)}
                      size="small"
                      color="error"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RepresentantSitesTable;
